import { useEffect, useState } from "react";
import { InputSwitch } from "primereact/inputswitch";
import localeText from "../../../../../locale";
import AdminService from "../../../../../service/AdminService";
import { useToastContext } from "../../../../../contexts/ToastContext/ToastContext";
import { getToken, redirectIfUnauthorized } from "../../../utils/adminUtils";

// Liga/desliga do faturamento automático (só para Admin), com a mesma regra do clássico:
// só dá para ligar quando a API permite (enableAllowed); o motivo do bloqueio fica visível.
const InvoicingControl = () => {
  const { showErrorToast, showSuccessToast } = useToastContext();
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [toggling, setToggling] = useState(false);

  const fetchStatus = async () => {
    setLoadingStatus(true);
    try {
      const res = await new AdminService().getInvoicingStatus(getToken());
      setStatus(res?.result || null);
    } catch (e) {
      if (redirectIfUnauthorized(e)) return;
      showErrorToast({ detail: "errorFetchingInvoicingStatus" });
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = async (checked) => {
    if (checked && !status?.enableAllowed) return;
    setToggling(true);
    try {
      const service = new AdminService();
      const res = checked
        ? await service.enableInvoicing(getToken())
        : await service.disableInvoicing(getToken());

      if (res?.messageCode && res.messageCode !== 200000 && res.messageCode !== 200001) {
        showErrorToast({
          code: res.messageCode,
          message: res.message,
          fallbackMessage: localeText("errorTogglingInvoicing"),
        });
      } else {
        showSuccessToast({
          detail: checked ? "invoicingEnabledSuccessfully" : "invoicingDisabledSuccessfully",
        });
      }
    } catch (e) {
      if (redirectIfUnauthorized(e)) return;
      showErrorToast({
        code: e?.response?.data?.messageCode,
        message: e?.response?.data?.message,
        fallbackMessage: localeText("errorTogglingInvoicing"),
      });
    } finally {
      setToggling(false);
      fetchStatus();
    }
  };

  const enabled = !!status?.enabled;
  const blocked = !enabled && !status?.enableAllowed;

  return (
    <section className="admin-report__invoicing">
      <div className="admin-report__invoicing-text">
        <div className="admin-report__invoicing-title">
          {localeText("automaticInvoicing")}
          {!loadingStatus && (
            <span className={`admin-badge admin-badge--${enabled ? "good" : "neutral"}`}>
              {localeText(enabled ? "adminReportsInvoicingOn" : "adminReportsInvoicingOff")}
            </span>
          )}
        </div>
        <p className="admin-muted">{localeText("automaticInvoicingDescription")}</p>
        {status?.blockedReason && (
          <p className="admin-report__invoicing-warning">
            <i className="pi pi-exclamation-triangle" aria-hidden="true" />
            {status.blockedReason}
          </p>
        )}
      </div>
      <span title={blocked ? status?.blockedReason || "" : ""}>
        <InputSwitch
          checked={enabled}
          onChange={(e) => toggle(e.value)}
          disabled={loadingStatus || toggling || blocked}
          aria-label={localeText("automaticInvoicing")}
        />
      </span>
    </section>
  );
};

export default InvoicingControl;
